import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import CurrencyInput from '../shared/CurrencyInput';
import TrustFilieBadge from '../shared/TrustFilieBadge';
import {
  addDependent,
  removeDependent,
  updateTaxPayments,
  updateLifeEvents,
  setPriorYearReturnId,
  markFieldOverride,
} from '../../store/questionnaireSlice';
import { documentApi, extractErrorMessage } from '../../services/api';

const lifeEventOptions = [
  { id: 'married', label: 'Got Married' },
  { id: 'divorced', label: 'Got Divorced' },
  { id: 'had-baby', label: 'Had a Baby' },
  { id: 'bought-home', label: 'Bought a Home' },
  { id: 'sold-home', label: 'Sold a Home' },
  { id: 'started-business', label: 'Started a Business' },
  { id: 'changed-jobs', label: 'Changed Jobs' },
  { id: 'retired', label: 'Retired' },
  { id: 'none-of-above', label: 'None of the Above' },
];

const relationshipOptions = [
  { value: 'child', label: 'Child' },
  { value: 'parent', label: 'Parent' },
  { value: 'relative', label: 'Relative' },
  { value: 'other', label: 'Other' },
];

interface TrustFilieToggleProps {
  enabled: boolean;
  onToggle: () => void;
}

function TrustFilieToggle({ enabled, onToggle }: TrustFilieToggleProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${
        enabled ? 'bg-indigo-100' : 'bg-gray-200'
      }`}
      title={enabled ? 'Filie will check this for you' : 'Enter manually'}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
          enabled ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

export default function AdditionalInfoStep() {
  const dispatch = useDispatch();
  const { answers, smartDefaults } = useSelector(
    (state: RootState) => state.questionnaire
  );

  const dependents = answers.dependents || [];
  const selectedEvents = answers.lifeEvents || [];
  const payments = answers.taxPayments;
  const hasReturnUploaded = answers.priorYearReturnId !== undefined;

  // Dependent inline form
  const [showDependentForm, setShowDependentForm] = useState(false);
  const [depName, setDepName] = useState('');
  const [depRelationship, setDepRelationship] = useState('child');
  const [depDob, setDepDob] = useState('');

  // Estimated payments trust-filie toggles
  const hasDetectedEstimated = smartDefaults?.estimatedPayments !== null && smartDefaults?.estimatedPayments !== undefined;
  const [trustEstimated, setTrustEstimated] = useState(!hasDetectedEstimated);
  const hasDetectedOverpayment = smartDefaults?.priorYearOverpayment !== null && smartDefaults?.priorYearOverpayment !== undefined;
  const [trustOverpayment, setTrustOverpayment] = useState(!hasDetectedOverpayment);

  // File upload state
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  const inputClasses =
    'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300';

  // Dependent handlers
  const handleAddDependent = () => {
    if (!depName.trim()) return;
    dispatch(
      addDependent({
        id: `dep_${Date.now()}`,
        name: depName.trim(),
        ssn: '',
        relationship: depRelationship,
        dateOfBirth: depDob,
      })
    );
    setDepName('');
    setDepRelationship('child');
    setDepDob('');
    setShowDependentForm(false);
  };

  const handleCancelDependent = () => {
    setDepName('');
    setDepRelationship('child');
    setDepDob('');
    setShowDependentForm(false);
  };

  // Estimated payments
  const estimatedQ1 =
    payments?.estimatedPayments?.q1 ??
    smartDefaults?.estimatedPayments?.q1 ??
    0;
  const estimatedQ2 =
    payments?.estimatedPayments?.q2 ??
    smartDefaults?.estimatedPayments?.q2 ??
    0;
  const estimatedQ3 =
    payments?.estimatedPayments?.q3 ??
    smartDefaults?.estimatedPayments?.q3 ??
    0;
  const estimatedQ4 =
    payments?.estimatedPayments?.q4 ??
    smartDefaults?.estimatedPayments?.q4 ??
    0;
  const priorOverpayment =
    payments?.priorYearOverpayment ??
    smartDefaults?.priorYearOverpayment ??
    0;

  const handleEstimatedChange = (
    quarter: 'q1' | 'q2' | 'q3' | 'q4',
    value: number
  ) => {
    dispatch(
      updateTaxPayments({
        estimatedPayments: {
          q1: quarter === 'q1' ? value : estimatedQ1,
          q2: quarter === 'q2' ? value : estimatedQ2,
          q3: quarter === 'q3' ? value : estimatedQ3,
          q4: quarter === 'q4' ? value : estimatedQ4,
        },
      })
    );
    dispatch(markFieldOverride(`estimated_${quarter}`));
  };

  const handleOverpaymentChange = (value: number) => {
    dispatch(updateTaxPayments({ priorYearOverpayment: value }));
    dispatch(markFieldOverride('priorYearOverpayment'));
  };

  // Life events
  const toggleLifeEvent = (id: string) => {
    if (id === 'none-of-above') {
      const isSelected = selectedEvents.includes('none-of-above');
      dispatch(updateLifeEvents(isSelected ? [] : ['none-of-above']));
    } else {
      const withoutNone = selectedEvents.filter((e) => e !== 'none-of-above');
      const isSelected = withoutNone.includes(id);
      const updated = isSelected
        ? withoutNone.filter((e) => e !== id)
        : [...withoutNone, id];
      dispatch(updateLifeEvents(updated));
    }
  };

  // File upload
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError(null);

    try {
      const response = await documentApi.upload(file);
      dispatch(setPriorYearReturnId(response.data.id));
      setUploadedFileName(file.name);
    } catch (err) {
      setUploadError(extractErrorMessage(err, 'Failed to upload file. Please try again.'));
    } finally {
      setUploading(false);
    }

    e.target.value = '';
  };

  const handleRemoveUpload = () => {
    dispatch(setPriorYearReturnId(undefined));
    setUploadedFileName(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          Additional Information
        </h2>
        <p className="text-gray-500 mt-1">
          Dependents, payments, and life changes
        </p>
      </div>

      {/* Dependents */}
      <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
        <h3 className="font-semibold text-gray-900 mb-3">Dependents</h3>

        {dependents.length > 0 && (
          <div className="space-y-2 mb-4">
            {dependents.map((dep) => (
              <div
                key={dep.id}
                className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {dep.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {dep.relationship}
                    {dep.dateOfBirth ? ` — DOB: ${dep.dateOfBirth}` : ''}
                  </p>
                </div>
                <button
                  onClick={() => dispatch(removeDependent(dep.id))}
                  className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {dependents.length === 0 && !showDependentForm && (
          <p className="text-sm text-gray-500 mb-3">No dependents added</p>
        )}

        {/* Inline add form */}
        {showDependentForm && (
          <div className="p-4 bg-white border border-gray-200 rounded-lg mb-3 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                value={depName}
                onChange={(e) => setDepName(e.target.value)}
                placeholder="Dependent's full name"
                className={inputClasses}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relationship
                </label>
                <select
                  value={depRelationship}
                  onChange={(e) => setDepRelationship(e.target.value)}
                  className={inputClasses}
                >
                  {relationshipOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={depDob}
                  onChange={(e) => setDepDob(e.target.value)}
                  className={inputClasses}
                />
              </div>
            </div>
            <div className="flex items-center space-x-2 pt-1">
              <button
                onClick={handleAddDependent}
                disabled={!depName.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Save
              </button>
              <button
                onClick={handleCancelDependent}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {!showDependentForm && (
          <button
            onClick={() => setShowDependentForm(true)}
            className="text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg px-4 py-2 hover:bg-white transition-colors"
          >
            + Add Dependent
          </button>
        )}
      </div>

      {/* Estimated Tax Payments */}
      <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">
            Estimated Tax Payments
          </h3>
          {hasDetectedEstimated && <TrustFilieBadge source="document" />}
          {!hasDetectedEstimated && (
            <div className="flex items-center space-x-2">
              {trustEstimated && (
                <span className="text-xs text-gray-400">Filie will check</span>
              )}
              <TrustFilieToggle
                enabled={trustEstimated}
                onToggle={() => setTrustEstimated(!trustEstimated)}
              />
            </div>
          )}
        </div>

        {trustEstimated && !hasDetectedEstimated ? (
          <p className="text-sm text-gray-400">
            Filie will determine if you made estimated payments
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(['q1', 'q2', 'q3', 'q4'] as const).map((q, i) => (
              <div key={q}>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Q{i + 1}
                </label>
                <CurrencyInput
                  value={
                    q === 'q1'
                      ? estimatedQ1
                      : q === 'q2'
                        ? estimatedQ2
                        : q === 'q3'
                          ? estimatedQ3
                          : estimatedQ4
                  }
                  onChange={(v) => handleEstimatedChange(q, v)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Prior Year Overpayment */}
      <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">
            Prior Year Overpayment Applied
          </h3>
          {hasDetectedOverpayment && <TrustFilieBadge source="document" />}
          {!hasDetectedOverpayment && (
            <div className="flex items-center space-x-2">
              {trustOverpayment && (
                <span className="text-xs text-gray-400">Filie will check</span>
              )}
              <TrustFilieToggle
                enabled={trustOverpayment}
                onToggle={() => setTrustOverpayment(!trustOverpayment)}
              />
            </div>
          )}
        </div>

        {trustOverpayment && !hasDetectedOverpayment ? (
          <p className="text-sm text-gray-400">
            Filie will check for any overpayment from last year
          </p>
        ) : (
          <CurrencyInput
            value={priorOverpayment}
            onChange={handleOverpaymentChange}
          />
        )}
      </div>

      {/* Life Events */}
      <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
        <h3 className="font-semibold text-gray-900 mb-1">Life Events</h3>
        <p className="text-xs text-gray-500 mb-3">
          Did any of these happen this tax year? Select all that apply.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {lifeEventOptions.map((event) => (
            <label
              key={event.id}
              className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-white cursor-pointer transition-colors"
            >
              <input
                type="checkbox"
                checked={selectedEvents.includes(event.id)}
                onChange={() => toggleLifeEvent(event.id)}
                className="w-4 h-4 text-gray-600 rounded cursor-pointer"
              />
              <span className="ml-3 text-sm text-gray-900">{event.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Prior Year Return Upload */}
      <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
        <h3 className="font-semibold text-gray-900 mb-1">
          Prior Year Tax Return
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          Upload your prior year 1040 for year-over-year comparisons (optional)
        </p>

        {hasReturnUploaded && uploadedFileName ? (
          <div className="flex items-center space-x-3 p-3 bg-white border border-gray-200 rounded-lg">
            <svg
              className="w-5 h-5 text-green-600 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm text-gray-900 flex-1">
              {uploadedFileName}
            </span>
            <button
              onClick={handleRemoveUpload}
              className="text-sm text-gray-400 hover:text-gray-600 font-medium transition-colors"
            >
              Remove
            </button>
          </div>
        ) : (
          <div>
            <label className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition-colors">
              {uploading ? 'Uploading...' : 'Choose PDF File'}
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                disabled={uploading}
                className="hidden"
              />
            </label>
            <p className="text-xs text-gray-400 mt-2">PDF files only</p>
          </div>
        )}

        {uploadError && (
          <p className="text-sm text-red-600 mt-2">{uploadError}</p>
        )}
      </div>
    </div>
  );
}
